import {Log} from '@aboutyou/mlm';
import {getIsDatadogInfoLogsConsentGiven} from '@aboutyou/storage';
import {
  getProductTileContentHeight,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {useBrowserVersion, useIsMacOsSafari, useOS} from '@aboutyou/utils';
import React, {FC, useCallback, useRef, useState, type JSX} from 'react';
import styled from 'styled-components';
import {ShallowRenderingState} from './utils';

interface ShallowRendererProps {
  id: string;
  /**
   * @deprecated should be removed as soon as the `sale_price_display` is not use any longer
   */
  activateAbTestIfInView?: (inView: boolean) => void;
  shallowRenderingState?: ShallowRenderingState;
  renderer: (props: {
    ref: React.RefObject<HTMLElement | null>;
    isInViewOnRemount: boolean;
  }) => JSX.Element;
  rootElement?: Element | null;
  rootMargin?: string;
  renderShallowElementCompAs?: keyof JSX.IntrinsicElements;
}

const shouldRenderShallowElement = (
  shouldRenderShallow: boolean,
  shallowTileHeight: number | undefined,
) => {
  return shouldRenderShallow && shallowTileHeight !== undefined;
};

export const ShallowRenderer: FC<ShallowRendererProps> = ({
  id,
  activateAbTestIfInView,
  shallowRenderingState,
  renderer,
  rootElement,
  rootMargin,
  renderShallowElementCompAs,
}) => {
  const {tileRef, shouldRenderShallow, shallowTileHeight, isInViewOnRemount} =
    useShouldShallowRender(
      id,
      shallowRenderingState,
      activateAbTestIfInView,
      rootElement,
      rootMargin,
    );

  if (shouldRenderShallowElement(shouldRenderShallow, shallowTileHeight)) {
    return (
      <ShallowElement
        style={
          {
            height: `${shallowTileHeight}px`,
            containIntrinsicSize: `0 ${shallowTileHeight}px`,
          } as React.CSSProperties
        }
        ref={tileRef as React.RefObject<HTMLDivElement | null>}
        data-testid={`shallow-${id}`}
        as={renderShallowElementCompAs}
      />
    );
  }
  return renderer({ref: tileRef, isInViewOnRemount});
};

const getAutomaticProductTileHeight = (contentHeight: number) => {
  return `
  calc(
   ${contentHeight}px +
      (
        (
            (var(--grid-width) - var(--grid-gap, 0) * (var(--grid-columns) - 1) - var(--stream-padding, 0px) * 2) /
              (var(--grid-columns))
          ) / (3 / 4)
      )
  )
  `;
};

const enhancedGridMeasureSelector = '[data-testid="enhancedGridMeasure"]';

const getEnhancedGridMeasureElement = (target: HTMLElement) => {
  return target.closest(enhancedGridMeasureSelector) as HTMLElement | null;
};

const hasMeasuredGridWidth = (enhancedGridMeasure: HTMLElement | null) => {
  return Boolean(
    enhancedGridMeasure?.style.getPropertyValue('--grid-width').trim(),
  );
};

interface PendingContainmentUpdate {
  target: HTMLElement;
  parentLi: HTMLElement;
  contentHeight: number;
}

const pendingContainmentByGridMeasure = new WeakMap<
  HTMLElement,
  Map<HTMLElement, PendingContainmentUpdate>
>();
const observerByGridMeasure = new WeakMap<HTMLElement, MutationObserver>();

const enableContainmentWithAutomaticHeight = ({
  target,
  parentLi,
  contentHeight,
}: {
  target: HTMLElement;
  parentLi: HTMLElement;
  contentHeight: number;
}) => {
  parentLi.style.setProperty(
    '--tile-height',
    getAutomaticProductTileHeight(contentHeight),
  );
  // The interactive attribute must be set on the direct parent node.
  if (target.parentNode instanceof Element) {
    target.parentNode.setAttribute('data-interactive', 'true');
  }
  target.setAttribute('data-enable-containment', 'true');
  target.setAttribute('data-containment-height-mode', 'auto');
};

const flushPendingContainmentUpdates = (enhancedGridMeasure: HTMLElement) => {
  if (!hasMeasuredGridWidth(enhancedGridMeasure)) {
    return;
  }
  const pendingUpdates =
    pendingContainmentByGridMeasure.get(enhancedGridMeasure);
  if (!pendingUpdates) {
    const observer = observerByGridMeasure.get(enhancedGridMeasure);
    if (observer) {
      observer.disconnect();
      observerByGridMeasure.delete(enhancedGridMeasure);
    }
    return;
  }
  pendingUpdates.forEach(({target, parentLi, contentHeight}) => {
    if (!target.isConnected || !parentLi.isConnected) {
      return;
    }
    const alreadyEnabled = target.getAttribute('data-enable-containment');
    if (alreadyEnabled) {
      return;
    }
    enableContainmentWithAutomaticHeight({target, parentLi, contentHeight});
  });
  pendingUpdates.clear();
  pendingContainmentByGridMeasure.delete(enhancedGridMeasure);
  const observer = observerByGridMeasure.get(enhancedGridMeasure);
  if (observer) {
    observer.disconnect();
    observerByGridMeasure.delete(enhancedGridMeasure);
  }
};

const ensureSharedGridWidthObserver = (enhancedGridMeasure: HTMLElement) => {
  const existingObserver = observerByGridMeasure.get(enhancedGridMeasure);
  if (existingObserver) {
    return;
  }
  const observer = new MutationObserver(() => {
    flushPendingContainmentUpdates(enhancedGridMeasure);
  });
  observer.observe(enhancedGridMeasure, {
    attributes: true,
    attributeFilter: ['style'],
  });
  observerByGridMeasure.set(enhancedGridMeasure, observer);
};

const queueContainmentUntilGridWidth = ({
  enhancedGridMeasure,
  target,
  parentLi,
  contentHeight,
}: {
  enhancedGridMeasure: HTMLElement;
  target: HTMLElement;
  parentLi: HTMLElement;
  contentHeight: number;
}) => {
  const pendingUpdates =
    pendingContainmentByGridMeasure.get(enhancedGridMeasure) ?? new Map();
  pendingUpdates.set(target, {target, parentLi, contentHeight});
  pendingContainmentByGridMeasure.set(enhancedGridMeasure, pendingUpdates);
  ensureSharedGridWidthObserver(enhancedGridMeasure);
  // if width is already there, apply immediately and clear pending entries
  flushPendingContainmentUpdates(enhancedGridMeasure);
};

function setProductTileContainmentHeight(
  rect: DOMRect | undefined,
  target: HTMLElement,
  osName: string | undefined,
  isMacOsSafari: boolean | undefined,
  browserVersion: string | undefined,
) {
  /**
   * We cannot enable containment on Safari versions below 26.1 due to a bug causing
   * flickering issues
   */
  if (
    osName === 'ios' &&
    isMacOsSafari &&
    browserVersion &&
    parseFloat(browserVersion) < 26.1
  ) {
    return;
  }
  const contentHeight = getProductTileContentHeight({
    tileWidth: rect?.width,
    totalTileHeight: rect?.height,
  });

  const parentLi = target.closest('li');

  if (!parentLi) {
    return;
  }

  const alreadyEnabled = target.getAttribute('data-enable-containment');
  if (alreadyEnabled) {
    return;
  }

  const enhancedGridMeasure = getEnhancedGridMeasureElement(target);
  const canUseAutomaticHeight = hasMeasuredGridWidth(enhancedGridMeasure);

  /**
   * Generally our product tiles are contained within <li> elements, especially
   * in streams. We would like to append it to the parent li so that all variants
   * of the product tile have the height available at the top level
   */
  if (canUseAutomaticHeight) {
    enableContainmentWithAutomaticHeight({target, parentLi, contentHeight});
    return;
  }

  if (enhancedGridMeasure) {
    queueContainmentUntilGridWidth({
      enhancedGridMeasure,
      target,
      parentLi,
      contentHeight,
    });
  }
}

const useShouldShallowRender = (
  visibilityId: string,
  shallowRenderingState?: ShallowRenderingState,
  // @deprecated should be removed as soon as the `sale_price_display` is not used any longer
  inViewCallback?: (inView: boolean) => void,
  rootElement?: Element | null,
  rootMargin?: string,
) => {
  const osName = useOS();
  const isMacOsSafari = useIsMacOsSafari();
  const browserVersion = useBrowserVersion();

  const isInViewOnRemount = useRef(
    shallowRenderingState
      ? shallowRenderingState.shouldBeVisibleOnInitialLoad({visibilityId})
      : false,
  );
  const [shouldRenderShallow, setShouldRenderShallow] = useState(
    () =>
      shallowRenderingState?.shouldShallowRenderTile({
        visibilityId,
        inView: isInViewOnRemount.current,
      }) ?? false,
  );
  // This is just a reference copy of the state so that we don't have to update the useInViewEffect callback due to state changes but can still access the current value inside
  const shouldRenderShallowNow = useRef(shouldRenderShallow);
  shouldRenderShallowNow.current = shouldRenderShallow;

  const {ref: tileRef} = useInViewEffect<HTMLElement>(
    useCallback(
      (inView, entry) => {
        if (shallowRenderingState) {
          // Prevent flickering caused by ad blocking extensions that hide the element right after it comes into view
          if (!inView) {
            const parent = entry?.target.closest('li');

            if (parent?.style.display === 'none') {
              if (getIsDatadogInfoLogsConsentGiven()) {
                Log.unSampledLog(
                  '[AD BLOCKING] Product tile hidden by parent',
                  {
                    logOnlyOnce: true,
                  },
                );
              }

              return;
            }
          }

          setShouldRenderShallow(
            shallowRenderingState.shouldShallowRenderTile({
              visibilityId,
              inView,
            }),
          );

          // the height should only update if the element rendered is the real element and not the placeholder to avoid wrong height. If the shallow element is rendered it can have a height of 0 which is otherwise stored and uses for a shallow render on the next re-mount
          if (
            !shouldRenderShallowElement(
              shouldRenderShallowNow.current,
              shallowRenderingState.getTileHeight(visibilityId),
            )
          ) {
            const target = entry?.target as HTMLElement;
            const rect = entry?.boundingClientRect;

            setProductTileContainmentHeight(
              rect,
              target,
              osName,
              isMacOsSafari,
              browserVersion,
            );

            shallowRenderingState.setShallowRenderingState({
              visibilityId,
              height: rect?.height,
              inView,
            });
          }
        }
        inViewCallback?.(inView);
      },
      [
        shallowRenderingState,
        visibilityId,
        inViewCallback,
        shouldRenderShallowNow,
        osName,
        isMacOsSafari,
        browserVersion,
      ],
    ),
    {
      threshold: 0,
      triggerOnce: false,
      rootMargin: rootMargin ?? '500px',
      root: rootElement,
    },
  );

  const shallowTileHeight = shallowRenderingState?.getTileHeight(visibilityId);

  return {
    tileRef,
    shouldRenderShallow,
    shallowTileHeight,
    isInViewOnRemount: isInViewOnRemount.current,
  };
};

const ShallowElement = styled.div`
  contain: strict;
  content-visibility: hidden;
`;
